import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  FormHelperText,
  MenuItem,
  Modal,
  Select,
  Typography,
} from '@material-ui/core';
import { LinkOff } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import ActionButton from '../../../../components/ActionButton';
import CampoMac from '../../../../components/CampoMac';
import SpinnerComponent from '../../../../components/SpinnerComponent';
import WarningBanner from '../../../../components/WarningBanner';
import { getControlliAssegnabili } from '../../../../utils/api/iot_assegnazioni_api';
import StatoSondaBadge, { AiutoStatoSonda } from './StatoSondaBadge';
import {
  ETICHETTA_DISASSOCIA,
  MODALITA_MULTI_PUNTO,
  TITOLO_MODIFICA,
  MODALITA_SINGOLA,
  descriviModalita,
  normalizzaControlliAssegnabili,
} from './assegnazioniIotUtils';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contenitore: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: 'white',
    color: 'black',
    padding: theme.spacing(4),
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '620px',
    maxHeight: '90vh',
  },
  campo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  pulsanti: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // `marginRight: 'auto'` keeps the status to the left of the buttons: appearing
  // does not shift them, so a click never lands on a target that has moved.
  statoSalvataggio: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: 'auto',
    color: theme.palette.text.secondary,
  },
  spinnerInline: { display: 'flex' },
  trasmissione: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  rimozione: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  disassocia: {
    flexShrink: 0,
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  errore: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  // La voce non assegnabile va a capo. `MenuItem` porta `overflow: hidden` e
  // `white-space: nowrap` e **nessun** `text-overflow`, quindi senza `normal` il
  // testo viene tagliato al bordo del Paper a meta' parola e senza puntini — e a
  // cadere e' sempre il motivo, che viene dopo il nome ed e' l'unica cosa che
  // dice perche' quella voce e' grigia. Il criterio di BSDEVPLAT-94 chiede che
  // compaia «con il perche' leggibile»: tagliato, compare senza.
  //
  // L'altezza qui non si blocca, al contrario della cella di tabella dello stesso
  // difetto: un popover ha gia' il suo `maxHeight` e il suo scorrimento verticale,
  // quindi si cresce sull'asse che gia' funziona.
  //
  // `opacity: 1` perche' `disabled` porta `opacity: 0.5`, cioe' nero al 50% su
  // bianco: #808080, che sta a 3.95:1 contro i 4.5:1 di WCAG AA. Mandare a capo un
  // testo che poi non si legge sposta il problema. La voce resta `disabled`: e' il
  // grigio a dire che non e' scegliibile, non e' il colore a impedirlo.
  voceNonAssegnabile: {
    whiteSpace: 'normal',
    display: 'block',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    opacity: 1,
    color: theme.palette.text.secondary,
  },
}));

const AssegnazioneIotModal = ({
  assegnazione,
  attrezzature,
  attrezzaturaPreselezionataId,
  erroreServer,
  onClose,
  onDisassocia,
  onSalva,
  open,
  salvataggioInCorso,
  snPreselezionato,
}) => {
  const classes = useStyles();
  const inRiassegnazione = Boolean(assegnazione);
  // The probe comes from the review queue (BSDEVPLAT-111): the voce is *of that
  // probe*, so the field is filled in and locked. It is not a reassignment: the
  // publication closed the old Assegnazione, this one is new — which is why the
  // attrezzatura and the Controllo are still to be chosen.
  const dallaCoda = !inRiassegnazione && Boolean(snPreselezionato);
  // Detection mode is an attribute of the Assegnazione (ADR-0005): a new one is
  // born SINGOLA, a reassignment keeps the mode the row already carries, or the
  // PUT would silently downgrade a MULTIPUNTO one.
  const modalita = inRiassegnazione
    ? (assegnazione.modalita ?? MODALITA_SINGOLA)
    : MODALITA_SINGOLA;

  const [sn, setSn] = useState(assegnazione?.sn ?? snPreselezionato ?? '');
  // The Controlli are asked for only once the probe is confirmed (blur), so a
  // half-typed MAC does not produce a request per keystroke. With the probe
  // coming from the queue there is nothing to type, so it is confirmed at once.
  const [snConfermato, setSnConfermato] = useState(
    assegnazione?.sn ?? snPreselezionato ?? '',
  );
  const [attrezzaturaId, setAttrezzaturaId] = useState(
    assegnazione?.attrezzaturaId ?? attrezzaturaPreselezionataId ?? '',
  );
  const [controlloId, setControlloId] = useState(
    assegnazione?.controlloId ?? '',
  );
  // Solo da un blur: i campi bloccati (riassegnazione, coda) non lo ricevono mai,
  // e il loro valore e' il `sn` che il server ha gia' accettato.
  const [macNonValido, setMacNonValido] = useState(false);
  const [controlli, setControlli] = useState([]);
  const [messaggioSonda, setMessaggioSonda] = useState(null);
  const [erroreControlli, setErroreControlli] = useState(null);
  const [caricamentoControlli, setCaricamentoControlli] = useState(false);
  // Il tetto della tendina. Il Paper del menu prende `minWidth` dal campo ma come
  // `maxWidth` solo `calc(100% - 32px)` della *finestra*: e' quel tetto, non la
  // modale, a decidere dove il testo viene tagliato — quindi il difetto compariva
  // o no secondo quanto era larga la finestra del tecnico. Si misura all'apertura
  // perche' la modale e' larga il 90% della finestra e non e' un numero fisso.
  const campoControlloRef = useRef(null);
  const [larghezzaCampoControllo, setLarghezzaCampoControllo] = useState(null);
  const misuraCampoControllo = () => {
    // In jsdom `clientWidth` e' 0: trattato come assente, altrimenti si otterrebbe
    // un menu a `maxWidth: 0`.
    const larghezza = campoControlloRef.current?.clientWidth;
    setLarghezzaCampoControllo(larghezza > 0 ? larghezza : null);
  };

  useEffect(() => {
    let attivo = true;

    if (attrezzaturaId === '' || attrezzaturaId == null) {
      setControlli([]);
      setMessaggioSonda(null);
      return undefined;
    }

    setCaricamentoControlli(true);
    // L'avviso vale per la coppia sonda/attrezzatura che l'ha prodotto: appena una
    // delle due cambia e' una risposta vecchia, e finche' non arriva quella nuova
    // tiene `Salva` spento per una sonda che nessuno sta piu' proponendo — il click
    // che corregge il MAC si perde senza che niente lo dica. Si spegne subito, e
    // `caricamentoControlli` copre l'attesa: senza quello si aprirebbe una finestra
    // in cui `Salva` e' vivo con i Controlli della sonda nuova ancora in volo,
    // perche' `controlloId` sopravvive al cambio di `sn` (solo
    // `handleAttrezzaturaChange` lo azzera). Spento prima di cancellare l'avviso,
    // cosi' non c'e' nemmeno un render in cui nessuno dei due blocca.
    setMessaggioSonda(null);
    setErroreControlli(null);
    getControlliAssegnabili(attrezzaturaId, snConfermato || null)
      .then((risposta) => {
        if (!attivo) return;
        const esito = normalizzaControlliAssegnabili(risposta);
        setControlli(esito.controlli);
        setMessaggioSonda(esito.messaggioSondaGiaAssegnata);
      })
      .catch((error) => {
        if (!attivo) return;
        setControlli([]);
        setErroreControlli(error.message);
      })
      .finally(() => {
        if (attivo) setCaricamentoControlli(false);
      });

    return () => {
      attivo = false;
    };
  }, [attrezzaturaId, snConfermato]);

  const isSelezionabile = (controllo) =>
    controllo.assegnabile ||
    // The Controllo this Assegnazione already feeds comes back as taken by its
    // own probe; it must stay selectable, or a reassignment could never keep it.
    // Il confronto e' sull'id del Controllo, non sulla chiave della sonda: `sn`
    // e `sondaSn` sono due serializzazioni indipendenti della stessa chiave e il
    // client non deve dedurre nulla dalla sua forma (ADR-0013).
    (inRiassegnazione &&
      String(controllo.id) === String(assegnazione.controlloId));

  const handleAttrezzaturaChange = (event) => {
    setAttrezzaturaId(event.target.value);
    setControlloId('');
  };

  /**
   * `sondaGiaAssegnata` non è un avviso da leggere e scavalcare: è il rifiuto che
   * la creazione riceverà. La stessa regola — «una sonda alimenta un solo
   * Controllo», spec infra §3 e ADR-0012 — che la lettura espone qui la applica
   * `IotAssegnazioneCrudService.crea` come errore, e il rifiuto è globale perché la
   * riga di stato è chiavata sul `sn`. Dietro l'avviso il salvataggio non può
   * riuscire, quindi non si offre.
   *
   * Non in riassegnazione: là la sonda *è* quella dell'Assegnazione che si sta
   * cambiando, il server la trova viva e lo dice, ma la scrittura di quel percorso
   * non applica questa regola — bloccare `Salva` renderebbe impossibile spostare
   * una sonda su un altro Controllo della stessa attrezzatura.
   */
  const sondaImpegnataAltrove = Boolean(messaggioSonda) && !inRiassegnazione;

  const salvabile =
    Boolean(sn.trim()) &&
    !macNonValido &&
    attrezzaturaId !== '' &&
    controlloId !== '' &&
    !sondaImpegnataAltrove &&
    !caricamentoControlli &&
    !salvataggioInCorso;

  const handleSalva = () => {
    onSalva({
      macAddress: sn.trim(),
      attrezzaturaId,
      controlloId,
      modalita,
    });
  };

  return (
    /* While a write is in flight the modal does not close: `Annulla`, backdrop and
       ESC would throw `erroreServer` away, and the server's refusal would reach a
       modal that no longer exists - leaving the technician without knowing whether
       anything was written. */
    <Modal
      open={open}
      onClose={onClose}
      disableBackdropClick={salvataggioInCorso}
      disableEscapeKeyDown={salvataggioInCorso}
      className={classes.modal}
    >
      <div className={classes.contenitore}>
        <Typography variant="h5">
          {inRiassegnazione
            ? TITOLO_MODIFICA
            : dallaCoda
              ? 'Riassegna la sonda a un controllo del manuale nuovo'
              : 'Nuova assegnazione della sonda'}
        </Typography>

        <div className={classes.campo} data-testid="campo-sonda">
          <Typography variant="subtitle2">Sonda</Typography>
          {/* L'aiuto dice cosa fare, non come funziona il server: che la chiave
              venga canonicalizzata lato server (ADR-0013) e' vero e non serve a
              chi compila il form. Quello che gli serve sapere e' che la forma in
              cui la copia non conta — e al blur il campo gliela mostra. */}
          <CampoMac
            value={sn}
            disabled={inRiassegnazione || dallaCoda}
            placeholder="MAC della sonda"
            onChange={(valore) => {
              setSn(valore);
              setMacNonValido(false);
            }}
            onBlur={(valore, valido) => {
              setMacNonValido(valore !== '' && !valido);
              // Un valore che non e' un MAC non chiede i Controlli per una sonda
              // che non esiste: l'attrezzatura si interroga senza sonda.
              setSnConfermato(valido ? valore : '');
            }}
            helperText={
              dallaCoda
                ? 'È la sonda della voce in revisione: da qui non si cambia.'
                : 'Scrivilo come è stampato sulla sonda: maiuscole e separatori non contano.'
            }
          />
        </div>

        {inRiassegnazione && (
          <div
            className={classes.trasmissione}
            data-testid="campo-trasmissione"
          >
            <Typography variant="subtitle2">
              <AiutoStatoSonda etichetta="Trasmissione" />
            </Typography>
            <StatoSondaBadge assegnazione={assegnazione} />
          </div>
        )}

        <div className={classes.campo} data-testid="campo-attrezzatura">
          <Typography variant="subtitle2">Attrezzatura</Typography>
          {/* Disabilitata in riassegnazione come il campo sonda, e per la stessa
              ragione: la PUT sposta il Controllo, mai il soggetto
              (`ModificaAssegnazioneIotRequest` non ha attrezzaturaId). Lasciarla
              modificabile faceva scegliere un'attrezzatura, rispondere 200 e non
              spostare niente. */}
          <Select
            value={attrezzaturaId}
            displayEmpty
            disabled={inRiassegnazione}
            onChange={handleAttrezzaturaChange}
          >
            <MenuItem value="" disabled>
              Seleziona un&apos;attrezzatura
            </MenuItem>
            {attrezzature.map((attrezzatura) => (
              <MenuItem key={attrezzatura.id} value={attrezzatura.id}>
                {attrezzatura.nome}
              </MenuItem>
            ))}
          </Select>
          {inRiassegnazione ? (
            /* Dice come si sposta davvero una sonda, che e' l'unica cosa che il
               tecnico puo' fare da qui: disassociare e riassociare. E' anche cio'
               che lascia traccia di entrambe le operazioni. */
            <FormHelperText>
              Da qui cambi solo il controllo. Per spostare la sonda su
              un&apos;altra attrezzatura, disassociala e riassociala là: restano
              tracciate entrambe le operazioni.
            </FormHelperText>
          ) : (
            /* BSS-1573: the list is filtered server-side (soloPubblicate), so a
               technician who just created an attrezzatura in the draft would
               otherwise search this menu and find nothing, with no way to learn
               why. Static rule, not per-row: the section cannot tell which rows
               were hidden. */
            <FormHelperText>
              Compaiono solo le attrezzature dell’ultima pubblicazione della
              Gestione Operativa: quelle aggiunte in bozza si potranno assegnare
              dopo la pubblicazione.
            </FormHelperText>
          )}
        </div>

        <div
          className={classes.campo}
          data-testid="campo-controllo"
          ref={campoControlloRef}
        >
          <Typography variant="subtitle2">Controllo alimentato</Typography>
          <Select
            value={controlloId}
            displayEmpty
            disabled={attrezzaturaId === '' || caricamentoControlli}
            onChange={(event) => setControlloId(event.target.value)}
            onOpen={misuraCampoControllo}
            MenuProps={{
              PaperProps: {
                style: larghezzaCampoControllo
                  ? { maxWidth: larghezzaCampoControllo }
                  : undefined,
              },
            }}
          >
            <MenuItem value="" disabled>
              Seleziona un controllo
            </MenuItem>
            {controlli.map((controllo) => (
              <MenuItem
                key={controllo.id}
                value={controllo.id}
                disabled={!isSelezionabile(controllo)}
                className={
                  isSelezionabile(controllo)
                    ? undefined
                    : classes.voceNonAssegnabile
                }
              >
                {/* La voce assegnabile resta la stringa nuda: `Select` rende il
                    valore scelto clonando i figli del MenuItem, e solo quelle
                    possono diventare valore — cosi' il campo chiuso non cambia.
                    Il motivo su una riga sua e non dopo un trattino: su due o tre
                    righe il trattino sparisce nel ritorno a capo e non si
                    distingue piu' dove finisce il nome. */}
                {isSelezionabile(controllo) ? (
                  controllo.nome
                ) : (
                  <>
                    <Typography variant="body1" component="div" color="inherit">
                      {controllo.nome}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color="inherit"
                    >
                      {controllo.motivo}
                    </Typography>
                  </>
                )}
              </MenuItem>
            ))}
          </Select>
          {caricamentoControlli && <SpinnerComponent size={20} />}
        </div>

        <div className={classes.campo} data-testid="campo-modalita">
          <Typography variant="subtitle2">Modalità di rilevazione</Typography>
          <Select value={modalita} disabled>
            <MenuItem value={modalita}>{descriviModalita(modalita)}</MenuItem>
          </Select>
          <FormHelperText>
            {modalita === MODALITA_MULTI_PUNTO
              ? 'Questa assegnazione usa più sonde sulla stessa attrezzatura: la modalità non si può cambiare.'
              : 'Una sonda per attrezzatura. Più sonde sulla stessa attrezzatura non sono ancora disponibili.'}
          </FormHelperText>
        </div>

        {sondaImpegnataAltrove && (
          <WarningBanner testId="avviso-sonda">{messaggioSonda}</WarningBanner>
        )}
        {/* Il messaggio del server dice dove la sonda sta lavorando, che e' la
            meta' utile, e non dice che con quella sonda non si salva: il grigio
            di `Salva` da solo non distingue «Controllo non ancora scelto» da
            «questa sonda e' rifiutata», ed e' lo stesso argomento della riga di
            stato a scrittura in volo qui sotto. Statica e non per riga: nomina i
            due gesti disponibili da questa schermata, e resta vera anche quando
            la sonda lavora in un esercizio che da qui il tecnico non raggiunge. */}
        {sondaImpegnataAltrove && (
          <Typography variant="body2">
            Con questa sonda non puoi salvare: liberala dal controllo che
            alimenta, oppure scrivine un’altra.
          </Typography>
        )}
        {erroreControlli && (
          <Typography variant="body2" className={classes.errore}>
            {erroreControlli}
          </Typography>
        )}
        {erroreServer && (
          <Typography variant="body2" className={classes.errore}>
            {erroreServer}
          </Typography>
        )}

        {!inRiassegnazione && (
          <Typography variant="body2">
            Il monitoraggio inizia alla prima temperatura rilevata entro i
            limiti del controllo: se l’attrezzatura è già in temperatura,
            subito; se si sta ancora raffreddando, quando ci arriva. Fino ad
            allora non vengono inviati avvisi.
          </Typography>
        )}

        <div className={classes.pulsanti}>
          {/* A disabled button does not tell «no Controllo chosen yet» from «the
              write is in flight», and the wait is several network round trips in a
              row. `role="status"` is not decoration: a few seconds of silence is
              exactly when whoever cannot see the spinner clicks again. */}
          {salvataggioInCorso && (
            <div className={classes.statoSalvataggio} role="status">
              <SpinnerComponent size={18} className={classes.spinnerInline} />
              <Typography variant="body2">Salvataggio in corso…</Typography>
            </div>
          )}
          <ActionButton
            secondaryButton
            label="Annulla"
            disabled={salvataggioInCorso}
            onClick={onClose}
          />
          <ActionButton
            label="Salva"
            disabled={!salvabile}
            onClick={handleSalva}
          />
        </div>

        {inRiassegnazione && onDisassocia && (
          <>
            <Divider />
            <div className={classes.rimozione}>
              <div>
                <Typography variant="subtitle1">
                  Rimuovi associazione
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Il controllo torna manuale e la sonda resta libera per
                  un&apos;altra attrezzatura.
                </Typography>
              </div>
              <Button
                size="small"
                variant="outlined"
                className={classes.disassocia}
                startIcon={<LinkOff />}
                disabled={salvataggioInCorso}
                onClick={() => onDisassocia(assegnazione)}
              >
                {ETICHETTA_DISASSOCIA}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

AssegnazioneIotModal.propTypes = {
  assegnazione: PropTypes.object,
  attrezzature: PropTypes.array.isRequired,
  attrezzaturaPreselezionataId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  erroreServer: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDisassocia: PropTypes.func,
  onSalva: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  salvataggioInCorso: PropTypes.bool,
  snPreselezionato: PropTypes.string,
};

export default AssegnazioneIotModal;
